import { useState } from 'react';
import { MapPin, Phone, Clock, Car } from 'lucide-react';

export const StaticMap = ({ branches, onBranchSelect }) => {
  const [hoveredBranch, setHoveredBranch] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const handlePinClick = (branch) => {
    setSelectedBranch(branch);
    onBranchSelect?.(branch);
  };

  return (
    <div className="relative" data-testid="static-map">
      {/* Map container with static background */}
      <div className="map-container aspect-[16/9] md:aspect-[21/9] w-full bg-soft-gray relative">
        {/* Static map image placeholder - styled background */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, #E5E5E0 0%, #F5F5F0 50%, #E5E5E0 100%)
            `
          }}
        >
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                linear-gradient(rgba(26,26,26,0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(26,26,26,0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* "Roads" - decorative lines */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,50 Q30,45 50,50 T100,50" stroke="rgba(26,26,26,0.1)" strokeWidth="0.5" fill="none" />
            <path d="M20,0 Q25,50 20,100" stroke="rgba(26,26,26,0.1)" strokeWidth="0.5" fill="none" />
            <path d="M60,0 Q55,50 60,100" stroke="rgba(26,26,26,0.1)" strokeWidth="0.5" fill="none" />
            <path d="M0,30 Q50,35 100,30" stroke="rgba(26,26,26,0.08)" strokeWidth="0.3" fill="none" />
            <path d="M0,70 Q50,65 100,70" stroke="rgba(26,26,26,0.08)" strokeWidth="0.3" fill="none" />
          </svg>
        </div>

        {/* Map pins */}
        {branches.map((branch) => (
          <div
            key={branch.id}
            className="map-pin"
            style={{
              left: `${branch.mapPosition.x}%`,
              top: `${branch.mapPosition.y}%`
            }}
            onMouseEnter={() => setHoveredBranch(branch)}
            onMouseLeave={() => setHoveredBranch(null)}
            onClick={() => handlePinClick(branch)}
            data-testid={`map-pin-${branch.id}`}
          >
            {/* Pin SVG */}
            <svg 
              width="32" 
              height="40" 
              viewBox="0 0 32 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className={`drop-shadow-lg transition-transform duration-200 ${
                selectedBranch?.id === branch.id ? 'scale-125' : ''
              }`}
            >
              <path 
                d="M16 0C7.164 0 0 7.164 0 16c0 12 16 24 16 24s16-12 16-24c0-8.836-7.164-16-16-16z" 
                fill={selectedBranch?.id === branch.id ? '#B87333' : '#1A1A1A'}
              />
              <circle cx="16" cy="14" r="6" fill="#F5F5F0"/>
            </svg>

            {/* Tooltip */}
            {hoveredBranch?.id === branch.id && (
              <div 
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-white rounded-xl shadow-premium-hover z-10 animate-fade-in"
                data-testid={`tooltip-${branch.id}`}
              >
                <p className="font-medium text-sm">{branch.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{branch.address}</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-copper">
                  <Clock className="w-3 h-3" />
                  <span>{branch.hours.split('|')[0]}</span>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Map label */}
        <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/90 rounded-full text-xs font-medium text-graphite shadow-sm">
          Monterrey y área metropolitana
        </div>
      </div>

      {/* Branch cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {branches.map((branch) => (
          <button
            key={branch.id}
            onClick={() => handlePinClick(branch)}
            className={`p-4 rounded-xl border text-left transition-all duration-300 ${
              selectedBranch?.id === branch.id
                ? 'border-copper bg-copper/5 shadow-premium'
                : 'border-border/50 bg-white hover:border-copper/50 hover:shadow-premium'
            }`}
            data-testid={`branch-card-${branch.id}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedBranch?.id === branch.id ? 'bg-copper text-cream' : 'bg-soft-gray text-graphite'
              }`}>
                <MapPin className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{branch.name}</p>
                <p className="text-sm text-muted-foreground truncate">{branch.address}</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{branch.hours.split('|')[0].trim()}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4" />
                <span>{branch.phone}</span>
              </div>
              {branch.parking && (
                <div className="flex items-center gap-2 text-sm text-muted-green">
                  <Car className="w-4 h-4" />
                  <span>Estacionamiento disponible</span>
                </div>
              )}
            </div>

            <a
              href={`https://maps.google.com/?q=${branch.coordinates.lat},${branch.coordinates.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4 text-center text-sm text-copper hover:text-copper-dark font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              Cómo llegar →
            </a>
          </button>
        ))}
      </div>
    </div>
  );
};

export default StaticMap;
